import React from 'react';
import { Box, Container, TextField, Button, Grid, Paper, Drawer, Typography, Tooltip} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import './App.css';

function InputForm({ generate }) {
  const { t, i18n } = useTranslation();
  const [formValue, setFormValue] = React.useState({
    chars: t('defaults.chars'),
    row_count: 20,
    col_count: 18,
    title: t('defaults.title'),
    description: t('defaults.description')
  });

  const [helpOpen, setHelpOpen] = React.useState(false);
  
  React.useEffect(() => {
    setFormValue(prevValue => ({
      ...prevValue,
      chars: t('defaults.chars'),
      title: t('defaults.title'),
      description: t('defaults.description')
    }));
  }, [i18n.language, t]);

  const submitHandler = e => {
      e.preventDefault();
      generate(formValue);
  };

  const changeHandler = e => {
      e.preventDefault();
      let newFormValue = {...formValue};
      newFormValue[e.target.id] = e.target.value;
      setFormValue(newFormValue);
  };

  const toggleDrawer = open => e => {
      setHelpOpen(open);
  };
   
  return (
    <form onSubmit={ submitHandler } className="noprint">
      <Grid container spacing={6}>
        <Grid item xs={4}>
          <TextField id="chars"
                     label={
                       <Tooltip title={t('form.charactersTooltip')}><span>{t('form.characters')}🛈</span></Tooltip>
                     }
                     value={formValue.chars}
                     onChange={changeHandler} fullWidth={true} />
        </Grid>
        <Grid item xs={4}>
          <TextField id="row_count"
                     label={t('form.rowCount')}
                     type="number"
                     value={formValue.row_count}
                     onChange={changeHandler} fullWidth={true} />
        </Grid>
        <Grid item xs={4}>
          <TextField id="col_count"
                     label={t('form.colCount')}
                     type="number"
                     value={formValue.col_count}
                     onChange={changeHandler}
                     fullWidth={true} />
        </Grid>
        <Grid item xs={4}>
          <TextField id="title"
                     label={t('form.title')}
                     value={formValue.title}
                     onChange={changeHandler}
                     fullWidth={true} />
        </Grid>
        <Grid item xs={4} fullWidth={true} />
        <Grid item xs={4} fullWidth={true} />
        <Grid item xs={12}>
          <TextField id="description"
             label={
                 <Tooltip title={t('form.descriptionTooltip')}><span>{t('form.description')}🛈</span></Tooltip>
             }
             fullWidth={true}
             multiline={true}
             onChange={changeHandler}
             value={formValue.description}
          />
        </Grid>
      </Grid>
      <Grid container spacing={6}>
        <Grid item xs={1} />
        <Grid item xs={12} style={{textAlign: 'end'}}>
          <Button variant="contained" color="primary" type="submit">
            {t('form.generate')}
          </Button>
          <Button variant="contained" color="primary" onClick={ window.print } style={{marginLeft: 36}}>
            {t('form.previewPrint')}
          </Button>
          <Button onClick={toggleDrawer(true)} style={{marginLeft: 36}}>{t('form.help')}</Button>
        </Grid>
        <Grid item xs={1} /> 
      </Grid>
      <Drawer open={helpOpen} onClose={toggleDrawer(false)} style={{}}>
        <div onClick={toggleDrawer(false)} style={{fontSize: '2em', margin: "10px 50px"}}>X</div>
        <div style={{margin: "10px 50px", width: 'min(90vw, 600px)'}}>
          <h2>{t('help.title')}</h2>
          <p>{t('help.intro')}</p>
          <p>{t('help.description')}</p>
          <h3>{t('help.usage')}</h3>
          <ul>
            {t('help.usageList', { returnObjects: true }).map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          <h3>{t('help.tips')}</h3>
          <ul>
            {t('help.tipsList', { returnObjects: true }).map((item, index) => (
              <li key={index}>
                {item}
                {index === 1 && (
                  <details>
                    <summary>{t('help.mathSymbols')}</summary>
                    <pre>∴ ∵ ∶ ∷ ∸ ∹ ∺ ∻ ∼ ∽ ∾ ∿ ≀ ≁ ≂ ≃ ≄ ≅ ≆ ≇ ≈ ≉ ≊ ≋ ≌ ≍ ≎ ≏ ≐ ≑ ≒ ≓ ≔ ≕ ≖ ≗ ≘ ≙ ≚ ≛ ≜ ≝ ≞ ≟</pre>
                  </details>
                )}
                {index === 1 && (
                  <details>
                    <summary>{t('help.arrowSymbols')}</summary>
                    <pre>← ↑ → ↓ ↔ ↕ ↖ ↗ ↘ ↙ ↚ ↛ ↜ ↝ ↞ ↟ ↠ ↡ ↢ ↣ ↤ ↥ ↦ ↧ ↨ ↩ ↪ ↫ ↬ ↭  ↮ ↯ ↰ ↱ ↲ ↳ ↴ ↵ ↶ ↷ ↸ ↹ ↺ ↻ ↼ ↽ ↾ ↿ ⇀ ⇁ ⇂ ⇃ ⇄ ⇅ ⇆ ⇇ ⇈ ⇉ ⇊ ⇋ ⇌ ⇍ ⇎ ⇏ ⇐ ⇑ ⇒ ⇓ ⇔ ⇕ ⇖ ⇗ ⇘ ⇙ ⇚ ⇛ ⇜ ⇝ ⇞ ⇟ ⇠ ⇡ ⇢ ⇣ ⇤ ⇥ ⇦ ⇧ ⇨ ⇩ ⇪ ⇫ ⇬ ⇭ ⇮ ⇯ ⇰ ⇱ ⇲ ⇳ ⇴ ⇵ ⇶ ⇷ ⇸ ⇹ ⇺ ⇻ ⇼ ⇽ ⇾ ⇿</pre>
                  </details>
                )}
                {index === 2 && (
                  <details>
                    <summary>{t('help.emoji')}</summary>
                    <pre>😀 😃 😄 😁 😆 😅 🤣 😂 🙂 🙃 😉 😊 😇</pre>
                  </details>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Drawer>
    </form>
  );
}

function CharTable({ table }) {
    return (<div>
      { 
        table.map(row => {
          return <p> { row.map( ch => <span> { ch } </span> ) } </p>
        })
      }
    </div>);
}

function App() {
  const { t } = useTranslation();
  const [table, setTable] = React.useState([]);
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");

  const choice = s => {
      let segmenter = new Intl.Segmenter();
      let seq = [...segmenter.segment(s)].map(x => x.segment)
      return seq[Math.floor(Math.random() * seq.length)]
  };

  const generate = formValue => {
    let ret = [];
    for(let row=0; row < formValue.row_count; row++){
      let line = []
      for(let col=0; col < formValue.col_count; col++){
        let c = choice(formValue.chars);
        line.push(c);
      }
      ret.push(line);
    };
    setTable(ret);
    setTitle(formValue.title);
    setDescription(formValue.description);
  };

  return (
    <Container fixed className="App">
      <Box className="noprint" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px'}}>
        <h1 style={{margin: 0, textAlign: 'left'}}>{t('appTitle')}</h1>
        <div>
          <LanguageSwitcher />
        </div>
      </Box>
      <InputForm generate={ generate } />
      <Paper elevation={0}>
        <Box textAlign="center"><h1>{ title }</h1></Box>
        <Box><Typography>{ description }</Typography></Box>
        <CharTable table={ table } />
      </Paper>
    </Container>
  );
}

export default App;
